export const F = { mono:"'IBM Plex Mono',monospace", head:"'Outfit',sans-serif", body:"'IBM Plex Sans',sans-serif" };

/* ═══════════════════════════════════════════════════════════════════════
   CURRENCY — follows the brand account, never a global default.
   INR groups in lakh/crore. Everything else uses K/M.
   ═══════════════════════════════════════════════════════════════════════ */
export const CURRENCY = {
  INR: { sym:'\u20B9', code:'INR', perEur:92.5,  locale:'en-IN' },
  EUR: { sym:'\u20AC', code:'EUR', perEur:1,     locale:'de-DE' },
  USD: { sym:'$',      code:'USD', perEur:1.08,  locale:'en-US' },
  GBP: { sym:'\u00A3', code:'GBP', perEur:0.85,  locale:'en-GB' },
};
export const FX_AS_OF = '21 Aug 2026';

let _cur = 'EUR';
export const setDisplayCurrency = c => { _cur = CURRENCY[c] ? c : 'EUR'; };
export const displayCurrency = () => _cur;

export const r2 = v => Math.round((+v || 0) * 100) / 100;
const t2 = v => (Math.round((+v || 0) * 100) / 100).toFixed(2).replace(/\.00$/, '');

/* Currency-aware. INR: Cr / L. Others: M / K. */
export const fC = v => {
  const n = +v || 0, c = CURRENCY[_cur], s = c.sym, a = Math.abs(n), sg = n < 0 ? '-' : '';
  if (_cur === 'INR') {
    if (a >= 1e7) return `${sg}${s}${t2(a/1e7)}Cr`;
    if (a >= 1e5) return `${sg}${s}${t2(a/1e5)}L`;
    if (a >= 1e3) return `${sg}${s}${t2(a/1e3)}K`;
    return `${sg}${s}${t2(a)}`;
  }
  if (a >= 1e6) return `${sg}${s}${t2(a/1e6)}M`;
  if (a >= 1e3) return `${sg}${s}${t2(a/1e3)}K`;
  return `${sg}${s}${t2(a)}`;
};
export const fN = v => {
  const a = Math.abs(+v || 0), sg = (+v||0) < 0 ? '-' : '';
  if (a >= 1e6) return `${sg}${t2(a/1e6)}M`;
  if (a >= 1e3) return `${sg}${t2(a/1e3)}K`;
  return `${sg}${Math.round(a)}`;
};
export const fPct = v => `${r2(v)}%`;
export const curSym = () => CURRENCY[_cur].sym;

/* ═══════════════════════════════════════════════════════════════════════
   BRAND ACCOUNTS — each carries its own currency, market and unit economics.
   Every figure below is generated FROM these, so GMV / Orders / AOV /
   ROAS / CAC / LTV always reconcile. Nothing is a free-floating random.
   ═══════════════════════════════════════════════════════════════════════ */
export const BRANDS = [
  { id:'all', slug:'all-brands', name:'All Brands', col:'#00D4FF', ind:'Portfolio',
    market:'Global', currency:'EUR', kind:'portfolio' },

  { id:'zenith', slug:'zenith-foods', name:'Zenith Foods', col:'#FF6B6B', ind:'FMCG / Q-Commerce',
    market:'India', currency:'INR', kind:'commerce',
    spend:24000000, roas:4.2, aov:420, margin:0.34, ntb:0.30, acqShare:0.40, repeat:7.5, tz:'Asia/Kolkata' },

  { id:'lumino', slug:'luminoskin', name:'LuminoSkin', col:'#C77DFF', ind:'Beauty / D2C',
    market:'Netherlands', currency:'EUR', kind:'commerce',
    spend:72000, roas:3.4, aov:58, margin:0.62, ntb:0.45, acqShare:0.55, repeat:4.2, tz:'Europe/Amsterdam' },

  { id:'bolt', slug:'boltev', name:'BoltEV', col:'#06D6A0', ind:'EV / Auto',
    market:'United Kingdom', currency:'GBP', kind:'commerce',
    spend:86000, roas:5.8, aov:1950, margin:0.24, ntb:0.95, acqShare:0.80, repeat:1.4, tz:'Europe/London' },

  { id:'stackr', slug:'stackr-saas', name:'Stackr SaaS', col:'#FFD166', ind:'B2B SaaS',
    market:'United States', currency:'USD', kind:'b2b',
    spend:61000, leads:780, acv:18400, margin:0.78, retentionYears:2.9, acqShare:0.85, tz:'America/New_York' },

  { id:'krishi', slug:'krishimart', name:'KrishiMart', col:'#F77F00', ind:'AgriTech Commerce',
    market:'India', currency:'INR', kind:'commerce',
    spend:4800000, roas:3.1, aov:1250, margin:0.34, ntb:0.55, acqShare:0.35, repeat:3.4, tz:'Asia/Kolkata' },
];
export const brandBySlug = slug => BRANDS.find(b => b.slug === slug);

/* Turns a real brand + its onboarding audit into a dashboard brand, so the
   numbers a user typed during the audit seed their own view and the business
   model decides which KPI set they get. */
const SLUG = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'brand';
export function brandFromAudit(brand, audit) {
  const a = audit || {};
  const model = brand.business_model || 'd2c_ecommerce';
  const kind = model === 'b2b_leadgen' ? 'b2b' : 'commerce';
  const currency = a.currency || 'USD';
  const budget = Number(a.target_budget) || Number(a.current_mrr) * 0.3 || 20000;
  const roas = Math.min(9, Math.max(1.4, Number(a.current_roas) || 3.2));
  let slug = SLUG(brand.name);
  if (BRANDS.some(x => x.slug === slug)) slug = `${slug}-${String(brand.id).slice(0, 4)}`;
  const base = {
    id: brand.id, slug, name: brand.name, col: '#00D4FF',
    ind: model === 'b2b_leadgen' ? 'B2B / Lead Gen' : model === 'hybrid' ? 'Hybrid' : 'D2C / E-Commerce',
    market: 'Your account', currency, kind, isReal: true, businessModel: model,
    northStar: a.north_star || null, websiteUrl: brand.website_url,
  };
  if (kind === 'b2b') {
    const acv = Math.max(1200, (Number(a.current_mrr) || 12000) * 1.5);
    return { ...base, spend: budget, leads: Math.max(60, Math.round(budget / 78)),
      acv, margin: 0.78, retentionYears: 2.9, acqShare: 0.85 };
  }
  const aov = Math.max(12, Math.round((Number(a.current_mrr) || 40000) / 700));
  return { ...base, spend: budget, roas, aov, margin: 0.55, ntb: 0.45, acqShare: 0.55, repeat: 3.6 };
}

export const ALL_PAID = [
  {id:"google",   name:"Google Ads",     col:"#4285F4", icon:"G",  cat:"search"},
  {id:"meta",     name:"Meta Ads",       col:"#1877F2", icon:"f",  cat:"social"},
  {id:"tiktok",   name:"TikTok Ads",     col:"#69C9D0", icon:"T",  cat:"social"},
  {id:"snap",     name:"Snapchat Ads",   col:"#FFFC00", icon:"S",  cat:"social"},
  {id:"pinterest",name:"Pinterest Ads",  col:"#E60023", icon:"P",  cat:"social"},
  {id:"twitter",  name:"X / Twitter Ads",col:"#1D9BF0", icon:"X",  cat:"social"},
  {id:"reddit",   name:"Reddit Ads",     col:"#FF4500", icon:"r",  cat:"social"},
  {id:"linkedin", name:"LinkedIn Ads",   col:"#0A66C2", icon:"in", cat:"b2b"},
  {id:"microsoft",name:"Microsoft Ads",  col:"#00B4D8", icon:"M",  cat:"search"},
  {id:"amazon",   name:"Amazon Ads",     col:"#FF9900", icon:"a",  cat:"ecom"},
  {id:"flipkart", name:"Flipkart Ads",   col:"#2874F0", icon:"F",  cat:"ecom"},
  {id:"youtube",  name:"YouTube Ads",    col:"#FF0000", icon:"Y",  cat:"video"},
  {id:"spotify",  name:"Spotify Ads",    col:"#1DB954", icon:"S",  cat:"audio"},
  {id:"apple",    name:"Apple Search",   col:"#A2AAAD", icon:"A",  cat:"search"},
  {id:"taboola",  name:"Taboola",        col:"#4A90E2", icon:"T",  cat:"native"},
  {id:"dv360",    name:"DV360/Prog.",    col:"#34A853", icon:"D",  cat:"prog"},
];

export const ECOM_PLAT = [
  {id:"amazon_in",name:"Amazon India",     col:"#FF9900",type:"marketplace"},
  {id:"flipkart", name:"Flipkart",         col:"#2874F0",type:"marketplace"},
  {id:"meesho",   name:"Meesho",           col:"#9B59B6",type:"marketplace"},
  {id:"nykaa",    name:"Nykaa",            col:"#FC2779",type:"marketplace"},
  {id:"myntra",   name:"Myntra",           col:"#FF3F6C",type:"marketplace"},
  {id:"ajio",     name:"AJIO",             col:"#FF6B35",type:"marketplace"},
  {id:"blinkit",  name:"Blinkit",          col:"#F5E642",type:"qcommerce"},
  {id:"zepto",    name:"Zepto",            col:"#8B5CF6",type:"qcommerce"},
  {id:"swiggy",   name:"Swiggy Instamart", col:"#FC8019",type:"qcommerce"},
  {id:"bigbasket",name:"BigBasket",        col:"#84C225",type:"qcommerce"},
  {id:"dunzo",    name:"Dunzo Daily",      col:"#00C2FF",type:"qcommerce"},
  {id:"jiomart",  name:"JioMart",          col:"#0052CE",type:"marketplace"},
  {id:"d2c",      name:"Own Website",      col:"#00D4FF",type:"d2c"},
];

export const SOURCES = [
  {name:"Google Ads",col:"#4285F4",ok:true,sync:"Live"},{name:"Meta Ads",col:"#1877F2",ok:true,sync:"15m"},
  {name:"TikTok Ads",col:"#69C9D0",ok:true,sync:"1h"},{name:"Snapchat Ads",col:"#FFFC00",ok:false,sync:"\u2014"},
  {name:"Pinterest Ads",col:"#E60023",ok:true,sync:"1h"},{name:"X/Twitter",col:"#1D9BF0",ok:true,sync:"1h"},
  {name:"Reddit Ads",col:"#FF4500",ok:false,sync:"\u2014"},{name:"LinkedIn Ads",col:"#0A66C2",ok:true,sync:"1h"},
  {name:"Microsoft Ads",col:"#00B4D8",ok:true,sync:"1h"},{name:"Amazon Ads",col:"#FF9900",ok:true,sync:"4h"},
  {name:"Flipkart Ads",col:"#2874F0",ok:true,sync:"4h"},{name:"GA4",col:"#E8710A",ok:true,sync:"Live"},
  {name:"GSC",col:"#34A853",ok:true,sync:"24h"},{name:"MS Clarity",col:"#00B4D8",ok:true,sync:"1h"},
  {name:"HubSpot",col:"#FF7A59",ok:true,sync:"30m"},{name:"Salesforce",col:"#00A1E0",ok:true,sync:"30m"},
  {name:"Zoho CRM",col:"#E42527",ok:true,sync:"1h"},{name:"Blinkit",col:"#F5E642",ok:true,sync:"4h"},
  {name:"Zepto",col:"#8B5CF6",ok:true,sync:"4h"},{name:"Swiggy IM",col:"#FC8019",ok:true,sync:"4h"},
  {name:"BigBasket",col:"#84C225",ok:true,sync:"4h"},{name:"Nykaa",col:"#FC2779",ok:true,sync:"4h"},
  {name:"Meesho",col:"#9B59B6",ok:true,sync:"4h"},{name:"Google Sheets",col:"#0F9D58",ok:true,sync:"5m"},
  {name:"YouTube Ads",col:"#FF0000",ok:true,sync:"1h"},{name:"Spotify Ads",col:"#1DB954",ok:false,sync:"\u2014"},
  {name:"Apple Search",col:"#A2AAAD",ok:false,sync:"\u2014"},{name:"DV360",col:"#34A853",ok:false,sync:"\u2014"},
  {name:"Taboola",col:"#4A90E2",ok:false,sync:"\u2014"},
];

/* A platform only carries spend if its source is connected. Showing spend
   against a disconnected platform is the fastest way to lose trust. */
export const PAID_SOURCE = {
  google:'Google Ads', meta:'Meta Ads', tiktok:'TikTok Ads', snap:'Snapchat Ads',
  pinterest:'Pinterest Ads', twitter:'X/Twitter', reddit:'Reddit Ads', linkedin:'LinkedIn Ads',
  microsoft:'Microsoft Ads', amazon:'Amazon Ads', flipkart:'Flipkart Ads', youtube:'YouTube Ads',
  spotify:'Spotify Ads', apple:'Apple Search', taboola:'Taboola', dv360:'DV360',
};
export const isPaidConnected = id => {
  const src = SOURCES.find(x => x.name === PAID_SOURCE[id]);
  return !!(src && src.ok);
};

export const TABS = [
  {id:"overview",  slug:"command-center", label:"Command Center", icon:"Zap",     mobileLabel:"Home"},
  {id:"paid",      slug:"paid-media",     label:"Paid Media",     icon:"DollarSign", mobileLabel:"Paid"},
  {id:"ecom",      slug:"ecom-qcom",      label:"E-Com & Q-Com",  icon:"ShoppingCart", mobileLabel:"E-Com"},
  {id:"organic",   slug:"organic-seo",    label:"Organic & SEO",  icon:"Leaf",    mobileLabel:"SEO"},
  {id:"crm",       slug:"crm-pipeline",   label:"CRM & Pipeline", icon:"Target",  mobileLabel:"CRM"},
  {id:"comp",      slug:"competitive",    label:"Competitive",    icon:"Eye",     mobileLabel:"Comp"},
  {id:"blindspots",slug:"blind-spots",    label:"Blind Spots",    icon:"AlertTriangle", mobileLabel:"Alerts"},
];
export const tabBySlug = slug => TABS.find(t => t.slug === slug);

export const SECTIONS = [
  {id:"kpis",label:"Top KPI Cards",tab:"overview"},
  {id:"gmv_trend",label:"GMV & Spend Trend",tab:"overview"},
  {id:"funnel",label:"Full Funnel",tab:"overview"},
  {id:"strongspots",label:"Strong Spots",tab:"overview"},
  {id:"blindspots",label:"Blind Spots",tab:"overview"},
  {id:"paid_table",label:"Paid Media Table",tab:"paid"},
  {id:"paid_charts",label:"Paid Media Charts",tab:"paid"},
  {id:"ecom_table",label:"E-Com Table",tab:"ecom"},
  {id:"ecom_charts",label:"E-Com Charts",tab:"ecom"},
  {id:"ga4",label:"GA4 Analytics",tab:"organic"},
  {id:"gsc",label:"GSC Keywords",tab:"organic"},
  {id:"clarity",label:"Clarity UX",tab:"organic"},
  {id:"crm_kpis",label:"CRM KPIs",tab:"crm"},
  {id:"crm_funnel",label:"Sales Funnel",tab:"crm"},
  {id:"comp_sov",label:"Competitive SOV",tab:"competitive"},
  {id:"bs_list",label:"Blind Spots List",tab:"blindspots"},
  {id:"ss_list",label:"Strong Spots List",tab:"blindspots"},
];

/* ═══════════════════════════════════════════════════════════════════════
   Deterministic generator. Same brand + range always yields the same
   numbers, so a figure never changes under the user between renders.
   ═══════════════════════════════════════════════════════════════════════ */
function seeded(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) { h = Math.imul(h ^ str.charCodeAt(i), 3432918353); h = (h << 13) | (h >>> 19); }
  let a = h >>> 0;
  return () => { a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
/* Kept for legacy call-sites (trend arrows only — never a headline figure). */
export const rnd = (a,b,d=0) => { const v=Math.random()*(b-a)+a; return d?+v.toFixed(d):Math.round(v); };

export const quarterly = (monthly, key, mode='sum') => {
  if (!Array.isArray(monthly) || monthly.length < 12) return [];
  const QS = [[0,3,'Q1','Jan\u2013Mar'],[3,6,'Q2','Apr\u2013Jun'],[6,9,'Q3','Jul\u2013Sep'],[9,12,'Q4','Oct\u2013Dec']];
  return QS.map(([s,e,q,label]) => {
    const slice = monthly.slice(s,e);
    const sum = slice.reduce((a,m)=>a + (+m[key] || 0), 0);
    const value = mode==='avg' ? r2(sum / Math.max(slice.length,1)) : r2(sum);
    return { q, label, value, months: slice.map(m => m.month).join(', ') };
  });
};

/* Where every KPI comes from, its formula, and which platforms feed it.
   Surfaced on the card so a CMO's analyst can reproduce the number. */
export const PROVENANCE = {
  'Total GMV':        { formula:'GMV = \u03A3 order value across all connected commerce platforms', sources:['Amazon','Flipkart','Blinkit','Zepto','Shopify','GA4'] },
  'Total Spend':      { formula:'Spend = \u03A3 media cost across all connected ad platforms', sources:['Google Ads','Meta Ads','Amazon Ads','LinkedIn Ads'] },
  'Blended ROAS':     { formula:'Blended ROAS = GMV \u00F7 total media spend', sources:['Google Ads','Meta Ads','Amazon Ads','GA4'] },
  'Blended ROI':      { formula:'ROI = (gross profit \u2212 spend) \u00F7 spend, at the brand gross margin', sources:['Google Ads','Meta Ads','Shopify'] },
  'Total Orders':     { formula:'Orders = GMV \u00F7 AOV, deduplicated across platforms', sources:['Shopify','Amazon','Blinkit','Zepto'] },
  'Avg SOV':          { formula:'Share of voice = your impressions \u00F7 category impressions', sources:['Google Ads','Amazon Ads','OmniTrack'] },
  'LTV : CAC':        { formula:'LTV = AOV \u00D7 gross margin \u00D7 repeat purchases. CAC = acquisition spend \u00F7 new customers', sources:['Shopify','HubSpot','Google Ads','Meta Ads'] },
  'Pipeline':         { formula:'Open pipeline = currently open opportunities \u00D7 average contract value. A point-in-time figure, so it does not grow with the lookback window.', sources:['HubSpot','Salesforce','LinkedIn Ads'] },
  'Revenue Leakage':  { formula:'Leakage = \u03A3 quantified impact of every open blind spot', sources:['All 23 connected sources','OmniTrack'] },
  'Avg Order Value':  { formula:'AOV = GMV \u00F7 orders', sources:['Shopify','Amazon','Blinkit'] },
};
export const provenanceFor = label => PROVENANCE[label] || null;
export const RANGE_LABEL = { '7d':'last 7 days', '30d':'last 30 days', '90d':'last 90 days', 'YTD':'year to date', '12m':'last 12 months' };
/* Month-over-month delta from the series actually plotted on the card. */
export const moTrend = (monthly, key) => {
  if (!Array.isArray(monthly) || monthly.length < 2) return 0;
  const cur = +monthly[monthly.length-1]?.[key] || 0, prev = +monthly[monthly.length-2]?.[key] || 0;
  return prev ? r2((cur - prev) / prev * 100) : 0;
};
export const LAST_SYNC_MIN = 14;

/* Seasonality: 12 monthly multipliers (Indian festive + Q4 peak). */
const SEASON = [0.88, 0.85, 0.94, 0.97, 1.02, 1.06, 0.99, 1.04, 1.12, 1.24, 1.18, 0.95];
const MO = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
/* A range selects real months out of the fixed 12-month series.
   7d takes a fraction of the latest month. */
const RANGE_WINDOW = { '7d':{months:1,frac:0.23}, '30d':{months:1,frac:1}, '90d':{months:3,frac:1},
                       'YTD':{months:8,frac:1}, '12m':{months:12,frac:1} };

/* Portfolio = the sum of the five brands, converted to EUR. */
function portfolioBase() {
  const eur = b => 1 / CURRENCY[b.currency].perEur;
  const kids = BRANDS.filter(b => b.kind !== 'portfolio');
  const commerce = kids.filter(b => b.kind === 'commerce');
  const b2b = kids.filter(b => b.kind === 'b2b');
  const spend = kids.reduce((a,b) => a + b.spend * eur(b), 0);

  // B2B contributes closed-won revenue, not GMV, so the roll-up is total revenue.
  const b2bWon = b2b.reduce((a,b) => a + b.leads * 0.35 * 0.30 * 0.55 * 0.24, 0);
  const b2bRev = b2b.reduce((a,b) => a + b.leads * 0.35 * 0.30 * 0.55 * 0.24 * b.acv * eur(b), 0);
  const b2bGross = b2b.reduce((a,b) => a + b.leads * 0.35 * 0.30 * 0.55 * 0.24 * b.acv * b.margin * eur(b), 0);

  const gmv = commerce.reduce((a,b) => a + b.spend * b.roas * eur(b), 0) + b2bRev;
  const orders = commerce.reduce((a,b) => a + (b.spend * b.roas) / b.aov, 0) + b2bWon;
  const grossProfit = commerce.reduce((a,b) => a + b.spend * b.roas * b.margin * eur(b), 0) + b2bGross;

  // CAC and LTV are both weighted by new customers, so LTV:CAC stays honest.
  const nc = b => ((b.spend * b.roas) / b.aov) * b.ntb;
  const newCust = commerce.reduce((a,b) => a + nc(b), 0);
  const acqSpend = commerce.reduce((a,b) => a + b.spend * b.acqShare * eur(b), 0);
  const ltv = commerce.reduce((a,b) => a + b.aov * b.margin * b.repeat * eur(b) * nc(b), 0) / newCust;

  const leads = commerce.reduce((a,b) => a + ((b.spend*b.roas)/b.aov) * 2.4, 0) + b2b.reduce((a,b) => a + b.leads, 0);
  const pipeline = b2b.reduce((a,b) => a + (b.leads * 0.35 * 0.30 - b.leads * 0.35 * 0.30 * 0.55 * 0.24) * b.acv * eur(b), 0);
  const totOrders = commerce.reduce((a,b) => a + (b.spend * b.roas) / b.aov, 0);
  const ntbPct = (newCust / totOrders) * 100;
  return { spend, gmv, orders, grossProfit, cac: acqSpend / newCust, ltv, leads, pipeline, ntbPct,
    marginPct: grossProfit / gmv, aov: gmv / orders };
}

function brandBase(b) {
  if (b.kind === 'portfolio') return portfolioBase();
  if (b.kind === 'b2b') {
    const leads = b.leads;
    const mql = leads * 0.35, sql = mql * 0.30, prop = sql * 0.55, won = prop * 0.24;
    const revenue = won * b.acv;
    return { spend:b.spend, gmv:revenue, orders:won, leads, mql, sql, prop, won, revenue,
      grossProfit: revenue * b.margin,
      pipeline: (sql - won) * b.acv,
      cac: (b.spend * b.acqShare) / won,
      ltv: b.acv * b.margin * b.retentionYears, aov: b.acv, ntbPct: 100, marginPct: b.margin };
  }
  const gmv = b.spend * b.roas;
  const orders = gmv / b.aov;
  const newCust = orders * b.ntb;
  return { spend:b.spend, gmv, orders, aov:b.aov, leads: orders * 2.4,
    grossProfit: gmv * b.margin, pipeline: 0, ntbPct: b.ntb * 100, marginPct: b.margin,
    cac: (b.spend * b.acqShare) / newCust,
    ltv: b.aov * b.margin * b.repeat };
}

export function makeData(brandId = 'all', range = '30d') {
  // Accepts a brand id from the demo set, or a real brand object built from
  // the user's own audit.
  const b = (brandId && typeof brandId === 'object')
    ? brandId
    : (BRANDS.find(x => x.id === brandId) || BRANDS[0]);
  setDisplayCurrency(b.currency);
  const rng = seeded(String(b.id));
  const base = brandBase(b);
  const isB2B = b.kind === 'b2b';
  const jitter = (amp = 0.06) => 1 + (rng() * 2 - 1) * amp;

  /* ── 12-month series, every field derived from the same monthly spend ── */
  const monthly = MO.map((m, i) => {
    const f = SEASON[i] * jitter();
    const spend = r2(base.spend * f);
    const orders = Math.round((isB2B ? base.won : base.orders) * f * jitter(0.05)) || 1;
    // B2B: revenue = deals x ACV exactly, so the ACV on screen never drifts.
    const gmv = isB2B ? r2(orders * base.aov) : r2(base.gmv * f * jitter(0.05));
    const grossProfit = r2(gmv * (base.marginPct ?? 0.4));
    const revenue = gmv;
    const leads = Math.round(base.leads * f * jitter(0.08));
    const cac = r2(base.cac * (2 - f) * jitter(0.05));
    const ltv = r2(base.ltv * jitter(0.04));
    const aov = orders ? r2(gmv / orders) : r2(base.aov);
    return {
      month: m, spend, gmv, revenue, orders, conversions: orders, leads, aov, cac, ltv,
      grossProfit,
      roas: spend ? r2(gmv / spend) : 0,
      acos: gmv ? r2(spend / gmv * 100) : 0,
      roi: spend ? r2((grossProfit - spend) / spend * 100) : 0,
      ltvCac: cac ? r2(ltv / cac) : 0,
      sessions: Math.round(orders * (isB2B ? 92 : 34) * jitter(0.1)) || Math.round(leads * 46),
      impressions: Math.round(spend * (b.currency === 'INR' ? 0.9 : 82) * jitter(0.12)),
      sov: r2(22 * f * jitter(0.1)),
      ntb: isB2B ? 100 : Math.min(100, r2(base.ntbPct * jitter(0.04))),
      pipeline: r2(base.pipeline * f * jitter(0.07)),  // trend shape only; the headline comes from the funnel
      leakage: r2(gmv * 0.062 * jitter(0.14)),
    };
  });

  /* ── Period totals: a real sum over the months the range covers ── */
  const win = RANGE_WINDOW[range] || RANGE_WINDOW['30d'];
  const window = monthly.slice(12 - win.months);
  const sum = k => r2(window.reduce((a, m) => a + (+m[k] || 0), 0) * win.frac);
  const totalSpend = sum('spend');
  const totalOrders = Math.round(sum('orders'));
  const totalLeads = Math.round(sum('leads'));
  // B2B revenue is exactly the deals on screen times the ACV on screen, and
  // gross profit is taken from that same figure so the margin never drifts.
  const totalGMV = isB2B ? r2(totalOrders * base.aov) : sum('gmv');
  const totalGross = isB2B ? r2(totalGMV * base.marginPct) : sum('grossProfit');
  const cac = r2(base.cac);
  const ltv = r2(base.ltv);
  const aov = isB2B ? r2(base.aov) : (totalOrders ? r2(totalGMV / totalOrders) : r2(base.aov));

  const kpis = {
    currency: b.currency, kind: b.kind, market: b.market,
    totalGMV, totalSpend, totalGross, aov,
    blendedROAS: totalSpend ? r2(totalGMV / totalSpend) : 0,
    blendedROI: totalSpend ? r2((totalGross - totalSpend) / totalSpend * 100) : 0,
    blendedACOS: totalGMV ? r2(totalSpend / totalGMV * 100) : 0,
    blendedTACOS: totalGMV ? r2(totalSpend / (totalGMV * 1.18) * 100) : 0,
    totalOrders, totalLeads,
    avgSOV: r2(window.reduce((a,m)=>a+m.sov,0) / window.length),
    totalNTB: r2(window.reduce((a,m)=>a+m.ntb,0) / window.length),
    cac, ltv, ltvCac: cac ? r2(ltv / cac) : 0,
    pipelineValue: 0,
    winRate: 0,
    avgBSR: Math.round(4 + rng() * 18),
    newCustomers: Math.round(totalOrders * (window[window.length-1].ntb / 100)),
    grossMargin: totalGMV ? r2(totalGross / totalGMV * 100) : r2((base.marginPct ?? 0.4) * 100),
  };
  // Leakage is a monthly run-rate and is labelled as such, so it does not scale with the range.
  kpis.revenueLeakage = r2(monthly.reduce((a,m)=>a+m.leakage,0) / 12);

  /* ── Paid platforms: the spend column sums to total spend and the revenue
        column sums to total GMV, so the table can never contradict the tiles.
        Disconnected platforms carry no spend at all. ── */
  const paidMixRaw = [0.26,0.21,0.06,0.01,0.03,0.02,0.01,0.07,0.04,0.13,0.06,0.05,0.01,0.01,0.01,0.02];
  const connected = ALL_PAID.map(p => isPaidConnected(p.id));
  const mixTotal = paidMixRaw.reduce((a,v,i) => a + (connected[i] ? v : 0), 0);
  const effs = ALL_PAID.map((_, i) => connected[i] ? 0.6 + rng() * 1.1 : 0);
  const spends = ALL_PAID.map((_, i) => connected[i] ? totalSpend * (paidMixRaw[i] / mixTotal) : 0);
  const weighted = spends.reduce((a, sp, i) => a + sp * effs[i], 0);
  const paid = ALL_PAID.map((p, i) => {
    if (!connected[i]) {
      return { ...p, connected:false, spend:0, revenue:0, impressions:0, clicks:0, conversions:0,
        roas:0, acos:0, tacos:0, ctr:0, cpc:0, cpm:0, cvr:0, cpa:0, sov:0, impressionShare:0,
        ntb:0, qualityScore:0, trend:0 };
    }
    const spend = r2(spends[i]);
    const revenue = r2(totalGMV * (spends[i] * effs[i]) / weighted);
    const cpm = r2((b.currency === 'INR' ? 110 : 9.4) * (0.7 + rng() * 0.9));
    const impressions = Math.round(spend / cpm * 1000) || 1;
    const ctr = r2(0.6 + rng() * 2.2);
    const clicks = Math.max(1, Math.round(impressions * ctr / 100));
    const cvr = r2(1.1 + rng() * 3.4);
    const conversions = Math.max(1, Math.round(clicks * cvr / 100));
    return { ...p, connected:true, spend, revenue, impressions, clicks, conversions,
      roas: spend ? r2(revenue / spend) : 0,
      acos: revenue ? r2(spend / revenue * 100) : 0,
      tacos: revenue ? r2(spend / (revenue * 1.18) * 100) : 0,
      ctr, cpc: clicks ? r2(spend / clicks) : 0, cpm,
      cvr, cpa: conversions ? r2(spend / conversions) : 0,
      sov: r2(6 + rng() * 34), impressionShare: r2(38 + rng() * 50),
      ntb: r2(kpis.totalNTB * (0.7 + rng() * 0.6)),
      qualityScore: Math.round(5 + rng() * 5), trend: r2(-18 + rng() * 52) };
  });

  /* ── Commerce platforms: GMV and orders both sum to the headline totals,
        so AOV reconciles on every row and in the footer. ── */
  const ecomMix = [0.19,0.14,0.05,0.07,0.05,0.03,0.13,0.10,0.06,0.05,0.02,0.03,0.08];
  const ecomGmv = ECOM_PLAT.map((_, i) => totalGMV * (ecomMix[i] ?? 0.02));
  const aovSkew = ECOM_PLAT.map(() => 0.75 + rng() * 0.6);
  const rawOrders = ecomGmv.map((g, i) => g / (aov * aovSkew[i]));
  const orderScale = totalOrders / (rawOrders.reduce((a,v)=>a+v,0) || 1);
  const scaledOrders = rawOrders.map(v => Math.max(1, Math.round(v * orderScale)));
  if (!isB2B) {
    const drift = totalOrders - scaledOrders.reduce((a,v)=>a+v,0);
    const biggest = scaledOrders.indexOf(Math.max(...scaledOrders));
    scaledOrders[biggest] = Math.max(1, scaledOrders[biggest] + drift);
  }
  const ecom = isB2B ? [] : ECOM_PLAT.map((p, i) => {
    const gmv = r2(ecomGmv[i]);
    const orders = scaledOrders[i];
    const cvr = r2(1.4 + rng() * 4.2);
    return { ...p, gmv, orders, aov: r2(gmv / orders), sessions: Math.max(orders, Math.round(orders / (cvr / 100))), cvr,
      returnRate: r2(3 + rng() * 16), cancelRate: r2(1.5 + rng() * 8),
      shareOfShelf: r2(6 + rng() * 32), sov: r2(8 + rng() * 34),
      avgRating: r2(3.8 + rng() * 1.1), reviews: Math.round(120 + rng() * 8200),
      bsr: Math.round(1 + rng() * 60), inventoryCoverage: Math.round(9 + rng() * 78),
      ntb: r2(kpis.totalNTB * (0.8 + rng() * 0.5)), repeatRate: r2(14 + rng() * 40),
      adSpend: r2(totalSpend * (ecomMix[i] ?? 0.02) * 0.8), visibility: r2(42 + rng() * 50),
      trend: r2(-18 + rng() * 52) };
  });

  /* ── CRM funnel: exact stage maths, B2B pipeline only for B2B brands ── */
  let ll, mq, sq, pr, ng, wn;
  if (isB2B) {
    wn = Math.max(1, totalOrders);
    ng = Math.round(wn / 0.39); pr = Math.round(ng / 0.62); sq = Math.round(pr / 0.55);
    mq = Math.round(sq / 0.30); ll = Math.round(mq / 0.35);
    kpis.totalLeads = ll;
  } else {
    ll = Math.max(1, totalLeads);
    mq = Math.round(ll * 0.35); sq = Math.round(mq * 0.30);
    pr = Math.round(sq * 0.55); ng = Math.round(pr * 0.62); wn = Math.round(ng * 0.39);
  }
  // Open pipeline is a point-in-time STOCK, so it is derived from the current
  // month only and does not inflate when the user widens the lookback.
  // Opportunity count is shown on both the tile and the CRM tab.
  const wnNow = Math.max(1, window[window.length-1].orders);
  const sqNow = Math.round(wnNow / 0.39 / 0.62 / 0.55);
  kpis.openOpportunities = isB2B ? Math.max(0, sqNow - wnNow) : 0;
  kpis.pipelineValue = isB2B ? r2(kpis.openOpportunities * b.acv) : 0;
  kpis.winRate = pr ? r2(wn / pr * 100) : 0;

  const crm = {
    funnel: [
      {stage:"Raw Leads",value:ll,col:"#00D4FF"},{stage:"MQL",value:mq,col:"#4285F4"},
      {stage:"SQL",value:sq,col:"#7B61FF"},{stage:"Proposal",value:pr,col:"#FFD166"},
      {stage:"Negotiation",value:ng,col:"#FF9F43"},{stage:"Closed Won",value:wn,col:"#06D6A0"},
    ],
    pipelineValue: kpis.pipelineValue,
    openOpportunities: kpis.openOpportunities,
    avgDealSize: isB2B ? r2(b.acv) : r2(aov),
    salesCycle: isB2B ? 74 : 3,
    winRate: kpis.winRate,
    cac, ltv, mqlToSql: 30, churn: isB2B ? 8.4 : 0,
  };

  const compSOV = [
    {name: b.kind === 'portfolio' ? 'Portfolio' : b.name, sov:r2(kpis.avgSOV), col:'#00D4FF'},
    {name:'Competitor A',sov:r2(kpis.avgSOV * 1.24),col:'#FF6B6B'},
    {name:'Competitor B',sov:r2(kpis.avgSOV * 0.78),col:'#FFD166'},
    {name:'Competitor C',sov:r2(kpis.avgSOV * 0.52),col:'#C77DFF'},
  ];
  const rest = r2(100 - compSOV.reduce((a,c)=>a+c.sov,0));
  compSOV.push({ name:'Others', sov: rest > 0 ? rest : 4.2, col:'#8a8a8a' });

  /* ── Blind spots sum EXACTLY to revenueLeakage ── */
  const bsWeights = [0.29,0.19,0.14,0.12,0.10,0.07,0.05,0.04];
  const COMMERCE_BS = [
    {title:"Q-Commerce Budget Underallocated",sev:"critical",ch:"Blinkit / Zepto",
     desc:"Quick commerce drives 34% of urban category growth but gets only 4.2% of budget.",
     action:"Shift 12% of DSP budget to Blinkit + Zepto brand stores."},
    {title:"Marketplace Organic Rank Decay",sev:"critical",ch:"Amazon / Flipkart",
     desc:"Top 3 SKUs slipped from page 1 to page 3. Competitor review velocity is up 4x.",
     action:"A+ content refresh plus sponsored brand video. Start this week."},
    {title:"Ad Spend Running on Out-of-Stock SKUs",sev:"critical",ch:"Google Ads / OmniTrack",
     desc:"Live campaigns are still bidding on 14 SKUs that are out of stock in 38 pincodes.",
     action:"Auto-pause bids when availability drops below 40% in a pincode cluster."},
    {title:"Mobile Checkout Leak",sev:"high",ch:"Own Website",
     desc:"68% of sessions are mobile, and cart abandonment there is 3.2x desktop.",
     action:"UX audit plus one-tap payments. Estimated CVR lift +1.8pp."},
    {title:"Branded Search Hijacked",sev:"high",ch:"Google Ads",
     desc:"Competitors are taking 38% impression share on your own brand terms.",
     action:"Launch a branded protection campaign with exact-match coverage."},
    {title:"Short-Form Video Audience Gap",sev:"high",ch:"TikTok / Reels",
     desc:"18-34 engagement is 3.2x your Meta average, with no short-form budget at all.",
     action:"Test a creator-UGC campaign at 3% of monthly spend."},
    {title:"Email Dormancy at 31%",sev:"medium",ch:"Email / CRM",
     desc:"4,200 contacts have been dormant for 90+ days with no win-back flow running.",
     action:"Three-step win-back sequence with re-permission."},
    {title:"120 Keywords Stuck at Position 5-15",sev:"medium",ch:"Organic SEO",
     desc:"120 keywords rank 5-15 with an average CTR of 1.1%.",
     action:"SEO sprint on the top 30 pages targeting featured snippets."},
  ];
  const B2B_BS = [
    {title:"Enterprise Segment Underfunded",sev:"critical",ch:"LinkedIn Ads",
     desc:"Enterprise leads close at 2.4x the ACV of mid-market but take 11% of spend.",
     action:"Move 15% of budget to an ABM programme against the top 200 accounts."},
    {title:"Demo No-Show Rate at 34%",sev:"critical",ch:"HubSpot",
     desc:"A third of booked demos never happen, so paid-acquired SQLs die before a first call.",
     action:"Two-step reminder sequence plus self-serve rescheduling."},
    {title:"Free Trial Activation Leak",sev:"critical",ch:"Product / GA4",
     desc:"61% of trials never complete the first key action, so acquisition spend never converts.",
     action:"Guided onboarding checklist and a day-2 activation nudge."},
    {title:"Branded Search Hijacked",sev:"high",ch:"Google Ads",
     desc:"Competitors are taking 38% impression share on your own brand terms.",
     action:"Launch a branded protection campaign with exact-match coverage."},
    {title:"Expansion Revenue Untracked",sev:"high",ch:"Salesforce",
     desc:"Seat expansion is not attributed, so payback on upsell campaigns is invisible.",
     action:"Add expansion as a pipeline type and attribute it to source."},
    {title:"Long Sales Cycle on Paid Leads",sev:"high",ch:"Paid / CRM",
     desc:"Paid-sourced deals take 74 days against 48 for referrals, tying up pipeline.",
     action:"Add a mid-funnel nurture track for paid-sourced SQLs."},
    {title:"Email Dormancy at 31%",sev:"medium",ch:"Email / CRM",
     desc:"4,200 contacts have been dormant for 90+ days with no win-back flow running.",
     action:"Three-step win-back sequence with re-permission."},
    {title:"120 Keywords Stuck at Position 5-15",sev:"medium",ch:"Organic SEO",
     desc:"120 keywords rank 5-15 with an average CTR of 1.1%.",
     action:"SEO sprint on the top 30 pages targeting featured snippets."},
  ];
  const bsMeta = isB2B ? B2B_BS : COMMERCE_BS;
  const blindSpots = bsMeta.map((m, i) => ({ id:i+1, ...m, impact: r2(kpis.revenueLeakage * bsWeights[i]) }));

  /* Strong spots reference the brand's own top channels, so an agri brand is
     never congratulated on a beauty marketplace. */
  const topEcom = [...ecom].sort((a,c) => c.gmv - a.gmv)[0];
  const paidByRoas = [...paid].filter(p => p.connected).sort((a,c) => c.roas - a.roas);
  const topPaid = paidByRoas[0];
  const runnerUp = paidByRoas.find(p => p.id !== topPaid?.id) || topPaid;
  const strongSpots = [
    {id:1,title:`${topPaid?.name || 'Google Ads'} ROAS Best-in-Class`,ch:topPaid?.name || 'Google Ads',
     metric:`ROAS ${topPaid?.roas ?? r2(kpis.blendedROAS*1.4)}x`,bench:`Your blend ${kpis.blendedROAS}x`,col:topPaid?.col || '#4285F4',
     desc:`Your most efficient connected channel, running ${r2(((topPaid?.roas || 0) / (kpis.blendedROAS || 1) - 1) * 100)}% above the blended average.`},
    isB2B
      ? {id:2,title:`${runnerUp?.name || 'LinkedIn Ads'} Lead Quality`,ch:runnerUp?.name || 'LinkedIn Ads',
         metric:`CPA ${fC(runnerUp?.cpa || 0)}`,bench:`Blended CAC ${fC(kpis.cac)}`,col:runnerUp?.col || '#0A66C2',
         desc:`${runnerUp?.name || 'This channel'} delivers ${fN(runnerUp?.conversions || 0)} conversions at a CPA well inside your blended CAC.`}
      : {id:2,title:`${topEcom?.name || 'Marketplace'} Category Dominance`,ch:topEcom?.name || 'Marketplace',
         metric:`SOV ${topEcom?.sov ?? kpis.avgSOV}%`,bench:`Your avg ${kpis.avgSOV}%`,col:topEcom?.col || '#FC2779',
         desc:`Your largest commerce channel at ${fC(topEcom?.gmv || 0)} GMV, ${r2((topEcom?.gmv || 0) / (totalGMV || 1) * 100)}% of the total.`},
    {id:3,title:"Email Is Your Highest-ROI Channel",ch:"Email",metric:"ROI 412%",bench:"Industry avg 180%",col:"#0F9D58",
     desc:"42% open rate, 8.6% CTR. Top 5% globally for the category."},
    {id:4,title:`New-to-Brand Share at ${kpis.totalNTB}%`,ch:"Cross-channel",metric:`NTB ${kpis.totalNTB}%`,
     bench:`Category avg ${r2(kpis.totalNTB * 0.72)}%`,col:"#C77DFF",
     desc:`${fN(kpis.newCustomers)} new customers in the period, acquired at ${fC(kpis.cac)} each.`},
    {id:5,title:"Organic Traffic Compounding",ch:"Organic SEO",metric:"+52% YoY",bench:"Industry avg +18%",col:"#34A853",
     desc:"38 keywords in the top 3. Zero-click impressions up 84%."},
    {id:6,title:`LTV:CAC Holding at ${kpis.ltvCac}x`,ch:"Unit economics",metric:`${kpis.ltvCac}x`,bench:"Healthy is 3x+",
     col: kpis.ltvCac >= 3 ? '#06D6A0' : '#FFD166',
     desc:`LTV ${fC(kpis.ltv)} against a CAC of ${fC(kpis.cac)} at a ${kpis.grossMargin}% gross margin.`},
  ];

  const offCount = SOURCES.filter(x => !x.ok).length;
  const insights = [
    `CRITICAL: ${fC(blindSpots[0].impact)} at risk \u2014 ${blindSpots[0].title.toLowerCase()} on ${blindSpots[0].ch}`,
    `CRITICAL: ${fC(blindSpots[1].impact)} at risk \u2014 ${blindSpots[1].title.toLowerCase()}`,
    `CRITICAL: ${fC(blindSpots[2].impact)} at risk \u2014 ${blindSpots[2].title.toLowerCase()}`,
    `ALERT: ${topPaid?.name} is your most efficient channel at ${topPaid?.roas}x while the blend sits at ${kpis.blendedROAS}x`,
    `ALERT: CAC is ${fC(kpis.cac)} against an LTV of ${fC(kpis.ltv)} \u2014 LTV:CAC ${kpis.ltvCac}x`,
    `ALERT: ${offCount} of ${SOURCES.length} sources are not connected, so this view is incomplete`,
    `WIN: ${kpis.totalNTB}% of ${isB2B ? 'revenue' : 'orders'} came from new customers this period`,
    `WIN: Gross margin holding at ${kpis.grossMargin}% on ${fC(kpis.totalGMV)} ${isB2B ? 'revenue' : 'GMV'}`,
    `WIN: Blended ROI at ${kpis.blendedROI}% on ${fC(kpis.totalSpend)} of media spend`,
  ];

  /* ── Organic, UX and competitive figures: generated here (deterministically)
        and derived from the same totals, so they neither drift on re-render nor
        contradict the Command Center. ── */
  const sessions = Math.round(sum('sessions'));
  const organic = {
    sessions,
    users: Math.round(sessions * 0.72),
    newUsers: r2(kpis.totalNTB),
    bounceRate: r2(34 + rng() * 22),
    avgDur: `${1 + Math.round(rng() * 3)}m ${10 + Math.round(rng() * 49)}s`,
    pps: r2(1.8 + rng() * 2.4),
    goalConv: totalOrders,
    convRate: sessions ? r2(totalOrders / sessions * 100) : 0,
  };
  const KW = ['best protein powder india','organic skincare','ev scooter price','b2b crm software',
              'agri input delivery','women wellness','saas marketing','q-commerce strategy'];
  const keywords = KW.map(kw => {
    const pos = r2(1 + rng() * 17);
    return { kw, pos, prev: r2(pos + (rng() * 6 - 3)),
      impr: Math.round(12000 + rng() * 168000), clicks: Math.round(800 + rng() * 17200),
      ctr: r2(0.8 + rng() * 3.7) };
  });
  const clarity = { rage: Math.round(120 + rng() * 720), dead: Math.round(80 + rng() * 340),
    qbacks: Math.round(200 + rng() * 1000), scroll: r2(42 + rng() * 36),
    err: Math.round(2 + rng() * 22), rec: Math.round(800 + rng() * 3400) };

  const paidSOV = r2(kpis.avgSOV * 1.18);
  const competitive = {
    overallSOV: r2(kpis.avgSOV),
    paidSOV,
    organicSOV: r2(Math.max(1, kpis.avgSOV * 2 - paidSOV)),
    rank: 1 + compSOV.filter(c => c.name !== 'Others' && c.sov > kpis.avgSOV).length,
    impShareLostBudget: r2(12 + rng() * 34),
    impShareLostRank: r2(8 + rng() * 22),
    byChannel: ALL_PAID.slice(0, 8).map(p => ({ channel: p.name,
      yours: r2(kpis.avgSOV * (0.7 + rng() * 0.7)),
      compA: r2(kpis.avgSOV * (0.9 + rng() * 0.8)),
      compB: r2(kpis.avgSOV * (0.5 + rng() * 0.6)) })),
  };
  const monthlyForecast = monthly.map(m => ({ ...m, forecast: Math.round(m.revenue * (1 + (0.05 + rng() * 0.17))) }));

  return { brand:b, range, monthly, monthlyForecast, organic, keywords, clarity, competitive,
    paid, ecom, crm, compSOV, kpis, blindSpots, strongSpots, insights,
    fx: { asOf: FX_AS_OF, note: `Portfolio converted to EUR at 1 EUR = 92.5 INR \u00B7 1.08 USD \u00B7 0.85 GBP on ${FX_AS_OF}` } };
}
